<template>
  <div class="flex w-full items-center gap-32">
    <slot />

    <div
      class="absolute top-[4%] flex max-h-[50%] w-full flex-col items-start gap-24 md:bottom-[10%] md:top-[10%] md:max-h-full"
      :class="isFullWidth ? $style.containerFull : $style.containerSmall"
    >
      <div
        class="flex min-h-full flex-col justify-center overflow-y-hidden md:h-full md:max-h-fit md:w-[50%]"
        :class="alignment"
      >
        <RichText
          v-if="title"
          class="text-static-default-hi mb-[3.3%] select-all md:mb-[1.7%]"
          :class="titleClass"
          :rich-text="title"
          style-variant="none"
        />

        <RichText
          v-if="subtitle"
          class="text-static-default-mid"
          :class="$style.subtitle"
          :rich-text="subtitle"
          style-variant="none"
        />

        <div v-if="link?.href && linkLabel" class="flex">
          <RevButton
            :class="getCTAColors(props.ctaStyling).button"
            :size="CTASize"
            variant="primary"
            @click="onCTAClick"
          >
            <span :class="getCTAColors(props.ctaStyling).text">
              {{ linkLabel }}
            </span>
          </RevButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useRouter } from '#imports'
import { computed, useCssModule } from 'vue'

import type { SliderSlide } from '@backmarket/http-api/src/api-specs-content/models/slider'
import { tw } from '@backmarket/utils/string/tw'
import type { Maybe } from '@backmarket/utils/types/Maybe'
import { RevButton } from '@ds/components/Button'

import RichText from '@backmarket/nuxt-layer-cms/RichText.vue'

import { getCTAColors } from '../../utils/CTAColorCombinations'

const props = withDefaults(
  defineProps<SliderSlide & { isFullWidth?: Maybe<boolean> }>(),
  {
    alignment: 'Left',
    fontType: 'Ivar',
    titleFontSize: 'L',
    ctaStyling: 'primary',
    linkLabel: '',
    isFullWidth: true,
  },
)

defineOptions({ inheritAttrs: false })

const router = useRouter()
const classes = useCssModule()

const alignment = computed(() => {
  const common = tw`items-center text-center w-[92%]`

  return props.alignment === 'Left'
    ? tw`md:items-start md:text-left mx-[4%] md:ml-[4%] ${common}`
    : tw`ml-[4%] mr-[4%] md:mx-auto ${common}`
})

const CTASize = computed(() => {
  return props.isFullWidth ? 'medium' : 'small'
})

const titleClass = computed(() => {
  switch (props.titleFontSize) {
    case 'S':
      return props.fontType === 'Ivar'
        ? tw`cms-heading-small-serif ${classes.titleS}`
        : tw`cms-heading-small-sans ${classes.titleS}`

    case 'M':
      return props.fontType === 'Ivar'
        ? tw`cms-heading-medium-serif ${classes.titleM}`
        : tw`cms-heading-medium-sans ${classes.titleM}`

    case 'L':
      return props.fontType === 'Ivar'
        ? tw`cms-heading-large-serif ${classes.titleL}`
        : tw`cms-heading-large-sans ${classes.titleL}`

    default:
      return ''
  }
})

async function onCTAClick(event: Event) {
  if (!props.link) return

  event.preventDefault()

  const resolved = router.resolve(props.link.href)
  if (resolved.matched.length) {
    await navigateTo(resolved.fullPath)

    return
  }

  const target = props.link.target || undefined
  window.open(props.link.href, target)
}
</script>

<style lang="css" module>
.containerSmall {
  container-type: inline-size;

  @media (min-width: theme('screens.md')) {
    container-type: size;
  }
}

.containerFull {
  container-type: inline-size;
}

.subtitle {
  font-size: 4.5cqmin;
  line-height: 1;
  margin-bottom: 6.7%;

  @media (min-width: theme('screens.md')) {
    font-size: 1.5cqmax;
    margin-bottom: 3.4%;
  }
}

.titleS {
  font-size: 9cqmin;
  line-height: 1;
  font-weight: 600;

  @media (min-width: theme('screens.md')) {
    font-size: 3.4cqmax;
  }
}

.titleM {
  font-size: 9cqmin;
  line-height: 1;
  font-weight: 600;

  @media (min-width: theme('screens.md')) {
    font-size: 3.9cqmax;
  }
}

.titleL {
  font-size: 9cqmin;
  line-height: 1;
  font-weight: 600;

  @media (min-width: theme('screens.md')) {
    font-size: 4.5cqmax;
  }
}
</style>
